<template>
  <div>
    <app-cells>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          shortcut="last30Days"
          :custom-shortcuts="custom_shortcuts"
          @is-hidden="fetchGraphList"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
      </div>
      <app-select
        @input="fetchGraphList"
        v-model="car_filter"
        :options="cars_options"
        :reduce="item => item.id"
        :display-field="option => `${option.car_model.name} (${option.plate_number})`"
        label="plate_number"
        placeholder="Cars"
        class="select--250"
        style="margin: 0"
      />
      <app-filters
        v-if="!is_general_manager"
        v-model="filter_status"
        :filters="filter_status_options"
        name="filterStatus"
        @change="onChangeFilter"
      />
    </app-cells>
    <app-spinner v-if="!loaded"/>
    <template v-else>
      <template v-if="is_general_manager">
        <div class="info-row">
          <div class="info-row__item">
            <div class="info-row__content">
              <span class="info-row__value">{{ graph_total_amount.toLocaleString() }} TSh</span>
              <span class="info-row__text">Total amount</span>
            </div>
          </div>
        </div>
        <div class="charts-grid">
          <highcharts :options="graph_options"/>
        </div>
      </template>
      <app-cells v-if="is_general_manager">
        <app-filters
          v-model="filter_status"
          :filters="filter_status_options"
          name="filterStatus"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table_columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__vendor>
          <div class="table-default__left">Vendor</div>
        </template>
        <template #h__car>
          <div class="table-default__left">Car</div>
        </template>
        <template #h__supplier>
          <div class="table-default__left">Supplier</div>
        </template>
        <template #efd="{ row }">
          <div class="table-default__left">
            <div class="table-default__controls">
              <template v-if="row.efd_receipt">
                <button v-if="row.efd_receipt.status && row.efd_receipt.status.code === 'pending'"
                        @click="onShowVerifyModal(row)"
                        v-tooltip="{ content: 'Verify', placement: 'top-end', offset: 10 }">
                  <img src="@/assets/img/check-green-icon.svg" alt="Verify">
                </button>
                {{ row.efd_receipt.verification_code }}
              </template>
              <template v-else>
                <button
                  v-if="!row.fund_request"
                  v-tooltip="{ content: 'Add receipt', placement: 'top-end', offset: 10 }"
                  @click="onOpenModal(row)"
                  :class="{'table-default__disabled': !row.vendor.supplier}"
                >
                  <img src="@/assets/img/add-big-button-icon.svg" alt="Add">
                </button>
                <router-link v-else
                             :to="{name: 'finance-fund-requests-detail-receipts', params: {id: row.fund_request.id }}"
                             class="table-default__link-border">
                  #{{ row.fund_request.id }}
                </router-link>
              </template>
            </div>
          </div>

        </template>
        <template #date="{ row }"><span style="white-space: nowrap">{{ dateTimeFormat(row.date) }}</span></template>
        <template #car="{ row }">
          <div class="table-default__left">
            {{ row.car.car_model.short_name }} ({{ row.car.plate_number }})
          </div>
        </template>
        <template #amount="{ row }"><span style="white-space: nowrap">{{ row.amount.toLocaleString() }} TSh</span>
        </template>
        <template #pp_litre="{ row }">{{ row.price_per_litre || '-' }}</template>
        <template #litres="{ row }">{{ row.litres.toLocaleString() }}</template>
        <template #type="{ row }">{{ row.fuel_type }}</template>
        <template #odometer="{ row }"><span style="white-space: nowrap">{{ row.odometer.toLocaleString() }} km</span>
        </template>
        <template #distance="{ row }">{{ row.distance.toLocaleString() }} km</template>
        <template #consumption="{ row }">{{ row.consumption.toLocaleString() }} l / 100 km</template>
        <template #vendor="{ row }">
          <div class="table-default__left">{{ row.vendor.name }}</div>
        </template>
        <template #supplier="{ row }">
          <div class="table-default__left">
            <template v-if="row.vendor.supplier">{{ row.vendor.supplier.trade_name }}</template>
            <span v-else class="status-tag" style="background-color: #e85c20">
              Missing supplier
            </span>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40"/>
      </app-cells>
    </template>
    <modal
      name="verify-modal"
      width="1200"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup" :key="repaint_key">
        <button @click="$modal.hide('verify-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Verify</div>
        <div class="popup__body">
          <app-flex-grid :rows="[[1, '275px']]" alignItems="start">
            <template #col-1-1>
              <div class="popup__holder">
                <vue-cropper
                  :src="verify_receipt"
                  dragMode="move"
                  class="view-cropper"
                  ref="cropper"
                />
                <!--                <vue-cropper-->
                <!--                  @ready="onCropperReady"-->
                <!--                  ref="cropper"-->
                <!--                  dragMode="move"-->
                <!--                  :src="require(`@/assets/img/photo_2025-06-03_17-38-33.jpg`)"-->
                <!--                  class="view-cropper"-->
                <!--                />-->
                <div class="popup__controls-holder">
                  <div class="popup__controls">
                    <!--                    <button @click="$refs.cropper.scale(1)" type="button">-->
                    <!--                      <img src="@/assets/img/rotate-arrow-to-the-left.svg" alt="">-->
                    <!--                    </button>-->
                    <!--                    <button @click="$refs.cropper.scale(1.2)" type="button">-->
                    <!--                      <img src="@/assets/img/plus-green-icon.svg" alt="">-->
                    <!--                    </button>-->
                    <button @click="$refs.cropper.rotate(-90)" type="button">
                      <img src="@/assets/img/rotate-arrow-to-the-left.svg" alt="">
                    </button>
                    <button @click="$refs.cropper.rotate(90)" type="button">
                      <img src="@/assets/img/rotate-arrow-to-the-right.svg" alt="">
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <template #col-1-2>
              <form @submit.prevent="onCheckForm('verify_form')">
                <app-form-group label="Verification code" label-for="code" required>
                  <app-input
                    @input.native="onInputCode('verify_form')"
                    v-model="verify_form.verification_code"
                    id="code"
                    placeholder="Verification code"
                    name="code"
                    :error="$v.verify_form.verification_code.$error"
                  />
                  <template #error>
                    <div v-if="$v.verify_form.verification_code.$dirty && !$v.verify_form.verification_code.required">
                      Required field
                    </div>
                    <div v-if="$v.verify_form.verification_code.$dirty && !$v.verify_form.verification_code.alphaNum">
                      Accepts only alphanumerics
                    </div>
                  </template>
                </app-form-group>
                <app-cells :indent="false" :wrap="false">
                  <app-form-group label="Date" required>
                    <app-date-picker
                      v-model="verify_form.date"
                      :clearable="false"
                      placeholder="Date"
                      :error="$v.verify_form.date.$error"
                    />
                    <template #error>
                      <div v-if="$v.verify_form.date.$dirty && !$v.verify_form.date.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Time" required>
                    <app-input
                      v-model="verify_form.time"
                      v-mask="'99:99:99'"
                      placeholder="00:00:00"
                      autocomplete="off"
                      id="time"
                      name="time"
                      @paste.native.prevent
                      :error="$v.verify_form.time.$error"
                    />
                    <template #error>
                      <div v-if="$v.verify_form.time.$dirty && !$v.verify_form.time.timeRegex">Wrong time</div>
                      <div v-if="$v.verify_form.time.$dirty && !$v.verify_form.time.required">Required field</div>
                    </template>
                  </app-form-group>
                </app-cells>
                <app-form-group label="Price per litre" label-for="price" required>
                  <app-input
                    v-model.number="verify_form.price_per_litre"
                    id="price"
                    placeholder="0"
                    name="price"
                    type="number"
                    :error="$v.verify_form.price_per_litre.$error"
                  />
                  <template #error>
                    <div v-if="$v.verify_form.price_per_litre.$dirty && !$v.verify_form.price_per_litre.required">
                      Required field
                    </div>
                  </template>
                </app-form-group>
                <app-cells position="center" :indent="false">
                  <app-button :disabled="$v.form.$error" type="submit" ref="submit">Verify</app-button>
                </app-cells>
              </form>
            </template>
          </app-flex-grid>
        </div>
      </div>
    </modal>
    <modal
      name="create-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('create-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Add receipt</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm()">
            <app-form-group label="Verification code" label-for="code" required>
              <app-input
                @input.native="onInputCode('form')"
                v-model="form.verification_code"
                id="code"
                placeholder="Verification code"
                name="code"
                :error="$v.form.verification_code.$error"
              />
              <template #error>
                <div v-if="$v.form.verification_code.$dirty && !$v.form.verification_code.required">Required field</div>
                <div v-if="$v.form.verification_code.$dirty && !$v.form.verification_code.alphaNum">
                  Accepts only alphanumerics
                </div>
              </template>
            </app-form-group>
            <app-cells :indent="false" :wrap="false">
              <app-form-group label="Date" required>
                <app-date-picker
                  v-model="form.date"
                  :clearable="false"
                  placeholder="Date"
                  :error="$v.form.date.$error"
                />
                <template #error>
                  <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Time" required>
                <app-input
                  v-model="form.time"
                  v-mask="'99:99:99'"
                  placeholder="00:00:00"
                  autocomplete="off"
                  id="time"
                  name="time"
                  @paste.native.prevent
                  :error="$v.form.time.$error"
                />
                <template #error>
                  <div v-if="$v.form.time.$dirty && !$v.form.time.timeRegex">Wrong time</div>
                  <div v-if="$v.form.time.$dirty && !$v.form.time.required">Required field</div>
                </template>
              </app-form-group>
            </app-cells>
            <app-form-group label="Price per litre" label-for="price" required>
              <app-input
                v-model.number="form.price_per_litre"
                id="price"
                placeholder="0"
                name="price"
                type="number"
                :error="$v.form.price_per_litre.$error"
              />
              <template #error>
                <div v-if="$v.form.price_per_litre.$dirty && !$v.form.price_per_litre.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Receipt copy" required>
              <app-uploader
                v-model="form.receipt_copy"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                class="custom-uploader--flex"
              />
              <template #error>
                <div v-if="$v.form.receipt_copy.$dirty && !$v.form.receipt_copy.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { format, parseISO } from 'date-fns'
import { Chart } from 'highcharts-vue'
import {
  getCarManagementCars,
  getCarManagementFuelLogs,
  getCarManagementFuelLogsGraph,
  patchCarManagementFuelLogEfdReceipts, patchCarManagementFuelLogVerify,
} from '@/http'
import { alphaNum, helpers, required } from 'vuelidate/lib/validators'
import VueCropper from 'vue-cropperjs'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])?$/i)

export default {
  name: 'CarManagementFuelLogList',
  components: { VueCropper, highcharts: Chart, VueCtkDateTimePicker },
  data() {
    return {
      loaded: false,
      filter_dates: null,
      table_preloader: false,
      rotate_count: 0,
      custom_shortcuts: [
        { key: 'last7Days', label: 'Last 7 days', value: 7 },
        { key: 'last14Days', label: 'Last 14 days', value: 14 },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
      ],
      graph_options: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      graph_total_amount: null,
      table: {
        items: [],
        columns: [],
        options: {
          headings: {
            efd: 'EFD',
            date: 'Date',
            car: 'Car',
            amount: 'Amount',
            pp_litre: 'Pricer per litre',
            litres: 'Litres',
            type: 'Fuel type',
            odometer: 'Odometer',
            distance: 'Distance',
            consumption: 'Consumption',
            vendor: 'Vendor',
            supplier: 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      cars_options: [],
      car_filter: null,
      form: {},
      verify_form: {},
      verify_receipt: null,
      log_to_action: null,
      repaint_key: null,
      filter_status: 'received',
      filter_status_options: [
        { value: 'received', label: 'Pending' },
        { value: 'missing', label: 'Missing' },
        { value: 'verified', label: 'Verified' },
        { value: '', label: 'All' },
      ],
    }
  },
  validations: {
    form: {
      verification_code: {
        required,
        alphaNum,
      },
      date: { required },
      time: { required, timeRegex },
      price_per_litre: { required },
      receipt_copy: { required },
    },
    verify_form: {
      verification_code: {
        required,
        alphaNum,
      },
      date: { required },
      time: { required, timeRegex },
      price_per_litre: { required },
    }
  },
  computed: {
    table_columns() {
      if (this.car_filter) return ['efd', 'date', 'car', 'amount', 'pp_litre', 'litres', 'type', 'odometer', 'distance', 'consumption', 'vendor', 'supplier']
      return ['efd', 'date', 'car', 'amount', 'pp_litre', 'litres', 'type', 'odometer', 'vendor', 'supplier']
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  mounted() {
    Promise.all([this.fetchItems(), this.fetchGraph()])
      .then(() => { this.loaded = true })
    getCarManagementCars().then(response => { this.cars_options = response.data })
  },
  methods: {
    fetchGraphList() {
      this.fetchItems()
      this.fetchGraph()
    },
    async fetchItems() {
      this.table_preloader = true
      const response = await getCarManagementFuelLogs({
        page: this.pagination.page,
        page_size: 40,
        car: this.car_filter,
        date_after: this.filter_dates?.start,
        date_before: this.filter_dates?.end,
        invalid_record: false,
        efd_status: this.filter_status,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    async fetchGraph() {
      if (!this.is_general_manager) return
      const response = await getCarManagementFuelLogsGraph({
        car: this.car_filter,
        from_date: this.filter_dates?.start,
        to_date: this.filter_dates?.end
      })
      this.graph_total_amount = response.data.total_amount
      this.graph_options.xAxis.categories = response.data.graph.categories
      this.graph_options.series = response.data.graph.series
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onCheckForm(type) {
      if (type !== 'verify_form') {
        this.$v.form.$touch()
        if (this.$v.form.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly',
          })
        } else {
          this.sendCreateForm()
        }
      } else {
        this.$v.verify_form.$touch()
        if (this.$v.verify_form.$invalid) {
          this.$notify({
            type: 'info',
            title: 'Check that the form fields are filled in correctly',
          })
        } else {
          this.sendVerifyForm()
        }
      }
    },
    sendCreateForm() {
      this.$refs.submit.preload = true
      patchCarManagementFuelLogEfdReceipts(this.log_to_action, {
        ...this.form,
        receipt_copy: this.form.receipt_copy.id
      })
        .then(() => {
          this.fetchGraphList()
          this.$notify({
            type: 'success',
            title: 'Receipt added',
          })
          this.$modal.hide('create-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    sendVerifyForm() {
      this.$refs.submit.preload = true
      patchCarManagementFuelLogVerify(this.log_to_action, {
        efd_date: this.verify_form.date,
        efd_time: this.verify_form.time,
        efd_verification_code: this.verify_form.verification_code,
        price_per_litre: this.verify_form.price_per_litre,
        efd_status: 'verified'
      })
        .then(() => {
          this.fetchGraphList()
          this.$notify({
            type: 'success',
            title: 'Receipt verified',
          })
          this.$modal.hide('verify-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowVerifyModal(row) {
      this.verify_form = {
        verification_code: row.efd_receipt.verification_code,
        date: row.efd_receipt.date,
        time: row.efd_receipt.time,
        price_per_litre: row.price_per_litre,
      }
      this.log_to_action = row.id
      this.verify_receipt = row.efd_receipt.receipt_copy.file
      this.$modal.show('verify-modal')
      this.repaint_key += 1
    },
    onOpenModal(log) {
      this.log_to_action = log.id
      this.form = {
        date: log.date.slice(0, 10)
      }
      this.$v.form.$reset()
      this.$modal.show('create-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onInputCode(form) {
      this[form].verification_code = this[form].verification_code.toUpperCase()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  }
}
</script>
